<template>
  <auth-container
    :title="title"
    :description="description"
    :titleColor="titleColor"
    :images="config.images.carousel"
    :mobileImages="config.images.carouselMobile"
  >
    <div class="reset-password">
      <v-card>
        <v-card-text>
          <v-form ref="form" class="pt-2" @submit.prevent="submit">
            <text-field
              ref="usernameField"
              v-show="showUsernameField"
              label="CPF ou CNPJ"
              v-model="username"
              :rules="rule.required"
              :error-messages="error.username"
              :mask="['###.###.###-##', '##.###.###/####-##']"
              required
            ></text-field>
            <text-field
              label="Senha temporária"
              v-model="model.temporaryPassword"
              :rules="rule.code"
              :error-messages="error.temporaryPassword"
              required
            ></text-field>
            <text-field
              label="Nova senha"
              v-model="model.newPassword"
              :rules="rule.password"
              :error-messages="error.passwords"
              type="password"
              required
            ></text-field>
            <text-field
              label="Confirmar senha"
              v-model="model.confirmPassword"
              :rules="rule.password"
              :error-messages="error.passwords || error.passwordAlreadyChanged"
              type="password"
              required
            ></text-field>
            <v-layout
              :column="mobile"
              :justify-space-around="!mobile"
              :align-center="mobile"
            >
              <v-btn type="submit" color="green" class="btn-md">
                <v-progress-circular
                  color="white"
                  indeterminate
                  v-if="loadingApp"
                ></v-progress-circular>
                <span v-else>Acessar o portal</span>
              </v-btn>
              <v-btn @click="resendCode" color="blue" class="btn-md">
                <v-progress-circular
                  color="white"
                  indeterminate
                  v-if="loadingApp"
                ></v-progress-circular>
                <span v-else>Reenviar senha</span>
              </v-btn>
              <v-btn
                flat
                class="reset-password__go-back btn-md"
                @click="$router.push({ name: 'login' })"
                >Ir para Login</v-btn
              >
            </v-layout>
          </v-form>
        </v-card-text>
      </v-card>
    </div>
  </auth-container>
</template>

<script>
import AuthContainer from '@/components/auth/Container';
import TextField from '@/components/fields/TextField';
import bus from '../../utils/bus';
import encrypt from '../../services/encrypt';
import { setToken } from '../../services/auth';
import { USER, UNIT, CONFIG, ROLES } from '../../utils/constants';
import { clearStore } from '../../services/storage';
import { removeAdminPreview } from '../../services/auth';
import { validateDocument } from '../../utils/documentValidation';

export default {
  name: 'access',
  components: {
    AuthContainer,
    TextField,
  },
  computed: {
    config() {
      return this.$store.getters[CONFIG.GET];
    },
  },
  data() {
    return {
      title: 'Nova senha',
      description: 'Cadastre sua senha para ter acesso ao Portal do Cliente.',
      emailMessage:
        'Senha enviada com sucesso. Sua senha de acesso foi enviada para o e-mail:',
      titleColor: '#dd2e36',
      error: {},
      username: '',
      userRole: ROLES.CLIENT_ROLE,
      showUsernameField: '',
      model: {
        temporaryPassword: '',
        newPassword: '',
        confirmPassword: '',
      },
    };
  },
  created() {
    this.userRole = this.$router.currentRoute.meta.userRole;
    clearStore();
    this.$store.commit(UNIT.MUTATE, []);
    this.$store.dispatch(UNIT.SELECT, null);
    this.$store.commit(USER.MUTATE_USER, null);
    removeAdminPreview();
    const code = this.$router.currentRoute.query.code;
    const email = this.$router.currentRoute.query.email;
    const username = this.$router.currentRoute.query.username;
    if (code) this.model.temporaryPassword = code;
    if (email) {
      this.description = `${this.emailMessage} ${this.hashEmail(
        email
      )}. Verifique também a sua caixa de SPAM.`;
    }
    if (username) {
      this.username = this.stripNonNumeric(username);
    }
    if (!email || !username) {
      this.showUsernameField = true;
    }
    this.$router.replace(this.$router.currentRoute.path);
  },
  methods: {
    displayNoUsernameErrorMessage() {
      bus.$emit('snackbar:open', {
        text: 'Ocorreu um erro, por favor acesse pelo link no e-mail: "Acesso ao Portal"',
        color: 'error',
        multiLine: true,
      });
    },
    passwordsCheck() {
      this.error = {};
      if (this.model.newPassword !== this.model.confirmPassword) {
        this.error.passwords = 'As senhas não conferem!';
        return false;
      }
      return true;
    },
    hashEmail(email) {
      const splittedEmail = email.split('@');
      splittedEmail[0] = [].map
        .call(splittedEmail[0], (char, charIndex) => {
          return charIndex === 0 || charIndex === splittedEmail[0].length - 1
            ? char
            : '*';
        })
        .join('');
      return splittedEmail[0] + '@' + splittedEmail[1];
    },
    async submit() {
      this.error = {};
      if (this.$refs.form.validate() && this.passwordsCheck()) {
        if (!this.username) {
          this.displayNoUsernameErrorMessage();
          return;
        }
        if (!validateDocument(this.username)) {
          this.error = this.enums.responses.invalidUsername;
          return;
        }
        const { confirmPassword, temporaryPassword } = this.model;
        const body = {
          username: this.username,
          password: encrypt(confirmPassword),
          confirmationCode: temporaryPassword,
          userRole: this.userRole,
        };
        const url = `${process.env.AUTH_API_URL}/confirmPassword`;
        try {
          const result = await this.requestService(url, 'POST', 
          {
            data: body,
            localErrorHandling: true
          });
          if (result) {
            const { token, user } = result;

            if (this.userRole == ROLES.SYNDIC_ROLE) {
              setToken(token, { keepLogged: this.keepLogged });

              this.$store.commit(USER.MUTATE_USER, user);

              await this.$store.dispatch(UNIT.SYNDIC_PRODUCTS, user);

              this.$router.push({ name: 'syndicSelectProduct' });
            } else {
              setToken(token);
              this.$store.commit(USER.MUTATE_USER, user);
              this.$router.push({ name: 'selectUnit' });
            }
            setAccessType(enums.accessTypes.login);
            this.createTrace(this.enums.logTraceActions.passwordChangeByCode);
          }
        } catch (err) {
          this.error = this.handleAuthError(err);
        }
      }
    },
    async resendCode() {
      this.error = {};
      if (this.$refs.usernameField.validate()) {
        if (!validateDocument(this.username)) {
          this.error = this.enums.responses.invalidUsername;
          return;
        }
        const url = `${process.env.AUTH_API_URL}/recoverPassword`;
        const body = {
          username: this.username,
          userRole: this.userRole,
        };
        try {
          const result = await this.requestService(url, 'POST', 
          {
            data: body,
            localErrorHandling: true
          });
          if (result) {
            bus.$emit('snackbar:open', {
              text: 'Código enviado com sucesso',
              color: 'green',
            });
          }
        } catch (err) {
          if (err && err.data && err.data.message) {
            if (err.data.message === 'This user has not been verified') {
              this.$router.push({
                name: 'login',
                params: { sendToSignUp: true, username: this.username },
              });
            }
          }
          this.error = this.handleAuthError(err);
        }
      } else {
        this.error.username = 'Você deve informar um CPF ou CNPJ válido';
      }
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/scss/breakpoint';

.reset-password {
  &__go-back {
    color: var(--label-color) !important;
    font-weight: 400;
  }

  .v-card {
    max-height: 486px;
  }

  .v-btn {
    min-width: 50px;
    @include breakpoint(not-mobile) {
      margin: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/auth/ResetPassword.vue